import { useEffect } from 'react';
import type { Feed, FeedEntry, UIComponent } from '~/services/layout/index.ts';
import {
  filterSkeletonEntries,
  generateSkeletonEntries,
  getLimit,
} from '~/services/layout/layout';
import { getIsPreferenceEditorByObserveStorageEndpointTag } from './preference-selector/use-preference-editor';

export function useFeedEntries({
  feedEntries,
  setFeedEntries,
  isLoading,
  isClient,
  feed,
  itemLimit,
  uiComponent,
  circularCondition,
  isClientStorage,
}: {
  feedEntries: FeedEntry[];
  setFeedEntries: any;
  isLoading?: boolean | undefined;
  isClient?: boolean | undefined;
  feed: Feed | undefined;
  itemLimit: number | undefined;
  uiComponent: UIComponent;
  circularCondition?: boolean;
  isClientStorage?: boolean;
}): FeedEntry[] {
  const feedEntriesLength = feed?.entry?.length || 0;

  itemLimit = getLimit(itemLimit);

  const loadingSkeletonEntriesLength: number | undefined =
    typeof itemLimit !== 'undefined' && itemLimit < 10 ? itemLimit : undefined;
  const generatedSkeletons = generateSkeletonEntries({
    length: loadingSkeletonEntriesLength,
  });

  const isPreferenceEditorByObserveStorageEndpointTag: boolean =
    getIsPreferenceEditorByObserveStorageEndpointTag({
      uiComponent: uiComponent!,
      feed,
    });

  useEffect(() => {
    if (isPreferenceEditorByObserveStorageEndpointTag) return;

    const newEntries: FeedEntry[] | [] =
      isLoading && isClient
        ? generatedSkeletons
        : feed?.entry?.slice(0, itemLimit) || [];

    if (!isLoading || !isClient) {
      setFeedEntries((prevFeedEntries: FeedEntry[]) =>
        keepPaginatedEntries(prevFeedEntries, newEntries)
      );
      return;
    }

    setFeedEntries((prevFeedEntries: any[]) => {
      return filterSkeletonEntries(prevFeedEntries);
    });
  }, [isLoading, feed]);

  if (isPreferenceEditorByObserveStorageEndpointTag) return feedEntries;

  if (feedEntriesLength === 0) return [];

  if (isClientStorage) return feed?.entry as FeedEntry[];

  const entries: FeedEntry[] | [] =
    isLoading && isClient
      ? generatedSkeletons
      : feed?.entry?.slice(0, itemLimit) || [];

  const res = feedEntries?.length > entries?.length ? feedEntries : entries;

  if (typeof circularCondition === 'undefined') return res;

  return circularCondition ? res : res.slice(0, itemLimit);
}

/**
 * Keeps pages appended by next-feed pagination when the feed is re-created
 * with the same first page (e.g. a parent group re-render passes a new feed
 * object), so the list does not snap back to its first page.
 * @param currentEntries - Entries currently in state, possibly with appended pages
 * @param baseEntries - First-page entries derived from the feed prop
 * @returns baseEntries followed by the appended pages when the first page is unchanged, otherwise baseEntries
 */
export function keepPaginatedEntries(
  currentEntries: FeedEntry[] | undefined,
  baseEntries: FeedEntry[]
): FeedEntry[] {
  if (!Array.isArray(currentEntries) || baseEntries.length === 0) {
    return baseEntries;
  }

  if (currentEntries.length <= baseEntries.length) return baseEntries;

  const appendedEntries: FeedEntry[] = filterSkeletonEntries(
    currentEntries.slice(baseEntries.length)
  );

  if (appendedEntries.length === 0) return baseEntries;

  const isSameFirstPage: boolean = baseEntries.every(
    (entry: FeedEntry, i: number) => entry?.id === currentEntries[i]?.id
  );

  if (!isSameFirstPage) return baseEntries;

  return [...baseEntries, ...currentEntries.slice(baseEntries.length)];
}

export function useFeedEntriesV1({
  initialFeedEntries,
  setFeedEntries,
}: {
  initialFeedEntries: FeedEntry[] | [];
  setFeedEntries: any;
}) {
  useEffect(() => {
    setFeedEntries(initialFeedEntries);
  }, []);
}
